import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router';
import { paths } from '../../common/constants/pathVariables';
import { DialogType } from '../../common/enums';
import {
  editSettingsMsg,
  instructionsForServiceMsg,
  instructionsMsg,
  numberSettingsMsg,
  seeMsg,
  t,
} from '../../common/i18n';
import { deepEqual } from '../../common/utils/objectUtils';
import { SubscriptionPbxDetails } from '../../generated/api/subscriptionPbxDetails';
import { SubscriptionStatusType } from '../../generated/api/subscriptionStatusType';
import { openDialog } from '../../public/site/dialogStore.js';
import { editSection, loadNumberRange } from '../../selfservice/actions';
import { DetailsWithCard } from '../DetailsWithInfoColumn/DetailsWithInfoColumn';
import { HeaderWithEditButton } from '../HeaderWithButton/HeaderWithButton';
import { PbxNumberSettingsAccordionContent } from '../PbxNumberSettingsAccordionContent/PbxNumberSettingsAccordionContent';
import type { DialogParams } from '../../common/types/dialog';
import type { Subscription } from '../../generated/api/subscription';
import type { State } from '../../selfservice/common/store.js';

export interface VoiceSubscriptionNumberSettingsProps {
  subscription: Subscription;
  hasPendingActions: boolean;
}

export const VoiceSubscriptionNumberSettings = ({
  subscription,
  hasPendingActions,
}: VoiceSubscriptionNumberSettingsProps) => {
  const dispatch = useDispatch();
  const numberRanges = useSelector((state: State) => state.resources?.numberRanges || {}, deepEqual);
  const { editingSection, errors, saving } = useSelector((state: State) => ({
    editingSection: state?.selfservice?.pendingSubscriptionActions?.editingSection || '',
    errors: state?.selfservice?.pendingSubscriptionActions?.errors,
    saving: state?.selfservice?.pendingSubscriptionActions?.saving,
  }));
  const numberRange = subscription?.details?.mobile?.pbxConfiguration?.corporateNumberRangeId
    ? numberRanges[subscription.details.mobile.pbxConfiguration.corporateNumberRangeId]
    : undefined;
  const extensionNumberRange = subscription?.details?.mobile?.pbxConfiguration?.extensionRangeId
    ? numberRanges[subscription.details.mobile.pbxConfiguration.extensionRangeId]
    : undefined;
  const isEditable =
    subscription.details!.specialNumber?.pbxConfigurationDetails.pbxType ===
      SubscriptionPbxDetails.PbxTypeEnum.TAVOITETTAVUUSKETJU ||
    subscription.details!.specialNumber?.pbxConfigurationDetails.pbxType === SubscriptionPbxDetails.PbxTypeEnum.VAKIO;
  const pbxConfiguration = subscription.details!.specialNumber!;
  const onShowDialog = (params: DialogParams) => openDialog(params);
  const onEditSectionIfNoActionsPending = (section: string) => {
    if (subscription?.subscriptionStatus === SubscriptionStatusType.IN_ACTIVATION) {
      onShowDialog({ type: DialogType.MACD_FORBIDDEN_SUBSCRIPTION_IN_ACTIVATION });
      return false;
    }
    if (hasPendingActions) {
      onShowDialog({ type: DialogType.SUBSCRIPTION_ACTION_PENDING });
      return false;
    }
    dispatch(editSection(section));
    return true;
  };
  const specialNumberOnEdit = (section: string) => {
    if (pbxConfiguration.corporateNumberRangeId && !numberRange) {
      dispatch(loadNumberRange(pbxConfiguration.corporateNumberRangeId));
    }

    if (pbxConfiguration.extensionRangeId && !extensionNumberRange) {
      dispatch(loadNumberRange(pbxConfiguration.extensionRangeId));
    }
    if (isEditable) {
      return onEditSectionIfNoActionsPending(section);
    }
    return onShowDialog({
      subscriptionId: subscription.subscriptionId,
      type: DialogType.SUBSCRIPTION_CHANGE_SPECIAL_NUMBER,
    });
  };

  return (
    <DetailsWithCard
      cardContent={
        <>
          <h3>{t.RD4R(instructionsMsg)}</h3>
          <p>
            {t.LJ10(seeMsg)}
            <Link to={paths.INSTRUCTIONS_VAKIO} target="_blank" rel="noopener noreferrer">
              {t.Z72R(instructionsMsg.toLowerCase())}
            </Link>
            {t.U63I(instructionsForServiceMsg)}
          </p>
        </>
      }
    >
      <HeaderWithEditButton
        title={t.AN17(numberSettingsMsg)}
        showButton={editingSection !== 'pbxNumberSettings'}
        buttonText={t.YBJF(editSettingsMsg)}
        onClick={() => specialNumberOnEdit('pbxNumberSettings')}
      />
      <PbxNumberSettingsAccordionContent
        editing={editingSection === 'pbxNumberSettings'}
        errors={errors}
        extensionNumberRange={extensionNumberRange}
        subscriptionPbxConfiguration={pbxConfiguration}
        numberRange={numberRange}
        onCancel={() => onEditSectionIfNoActionsPending('')}
        pbxSolution={subscription}
        saving={saving ?? false}
        subscriptionId={subscription.subscriptionId}
        subscriptionDisplayId={subscription.subscriptionDisplayId}
      />
    </DetailsWithCard>
  );
};
